import { useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';

import type { IStandardErrorResponse } from 'src/api/common';
import { useCreateActivity } from 'src/services/api/activities';
import type { ISendMessageRequest } from 'src/services/api/messages/use-send-message';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { SEND_MESSAGE_PATH } from 'src/services/api/messages/use-send-message';
import { getSendErrorMessage } from 'src/services/api/utils';
import pluralize from 'pluralize';

type ISendMessagesRequest = ISendMessageRequest[];

type ISendMessagesError = {
    errors: IStandardErrorResponse[];
    successCount: number;
    totalCount: number;
};

export const useSendMessages = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { mutateAsync: createActivity } = useCreateActivity();

    return useMutation<
        void,
        ISendMessagesError,
        ISendMessagesRequest
    >(
        async (payloads) => {
            const res = await Promise.allSettled(payloads.map((payload) => {
                const json = {
                    lead_uuid: payload.leadUuid,
                    channel: payload.channel,
                    contents: payload.message,
                    is_bulk_send: true,
                    ...(payload.snippet_id != null ? { snippet_id: payload.snippet_id } : {}),
                };

                return apiClient(SEND_MESSAGE_PATH, {
                    method: 'post',
                    json,
                });
            }));

            const errors = res.filter((r) => r.status === 'rejected');
            if (errors.length > 0) {
                const successCount = res.filter((r) => r.status === 'fulfilled').length;
                throw {
                    errors: errors.map(o => o.reason),
                    successCount,
                    totalCount: payloads.length,
                };
            }
        }, {
            onSuccess: (_data, variables) => {
                addNotification(`${variables.length} ${pluralize('message', variables.length)} sent successfully`, 'success');
                createActivity({ time: dayjs().format(), type: 'bulk_message' }).catch(() => {
                    console.error('Failed to create activity');
                });

                queryClient.invalidateQueries(queryKeys.conversations);
                queryClient.invalidateQueries(queryKeys.achievements);
                for (const payload of variables) {
                    queryClient.invalidateQueries([queryKeys.messages, payload.leadUuid]);
                }
            },
            onError: ({ errors, successCount, totalCount }, variables) => {
                const errorDetail = Array.from(
                    new Set(errors.map((err) => getSendErrorMessage(err, '')))
                ).filter(Boolean).join(', ');

                if (successCount > 0) {
                    addNotification(`${successCount} of ${totalCount} messages sent. ${errorDetail}`, 'warning');
                } else {
                    addNotification(errorDetail || 'Failed to send messages', 'error');
                }

                queryClient.invalidateQueries(queryKeys.conversations);
                for (const payload of variables) {
                    queryClient.invalidateQueries([queryKeys.messages, payload.leadUuid]);
                }
            }
        }
    );
};
